/**
 * @file miniAppBridge.js
 * @description
 * Pure decision logic for the desktop mini apps (Happy Time Tracker / Happy Team
 * Chat): what the menu-bar / tray text says and when a native notification is
 * worth showing. Kept free of React and of `@tauri-apps/*` so it is unit-testable
 * and shared by the Time Tracker and Team Chat bridges
 * (`components/miniApp/MiniAppBridge`).
 */

import {describeTask} from './unifiedTimer';

/** How often the tray text is refreshed. Minute resolution, so a throttled hidden webview still keeps up. */
export const TRAY_REFRESH_MS = 15000;

/** How often the running timer is re-read from the server (another device may have stopped it). */
export const TIMER_SYNC_MS = 60000;

/**
 * `H:MM` clock for the tray — seconds would force a once-a-second native call
 * from a webview the OS throttles as soon as its window is hidden.
 * @param {number} seconds Elapsed seconds.
 * @returns {string} e.g. `1:05`.
 */
export const formatTrayClock = (seconds) => {
    const total = Math.max(0, Math.floor(Number(seconds) || 0));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);

    return `${hours}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Menu-bar text for the current tracker timer.
 * @param {object|null} timer Facade timer from `/widgets/summary` (`fetchTrackerState().timer`).
 * @param {number} [now] Reference epoch ms (injectable for tests).
 * @returns {string|null} `●  1:05` running, `❚❚ 0:20` paused / on break, `null` when idle (clears the text).
 */
export const buildTrayTitle = (timer, now = Date.now()) => {
    const descriptor = describeTask(timer, now);

    if (!descriptor) {
        return null;
    }

    const clock = formatTrayClock(descriptor.elapsedSec);

    return descriptor.state === 'running' ? `● ${clock}` : `❚❚ ${clock}`;
};

/**
 * Whether a rise in the unread total deserves a native notification: only for a
 * real increase after the first reading, and never while the user is looking at
 * the window (the in-app UI already shows it).
 * @param {{previous: number|null, next: number, focused: boolean}} input Counts and window focus.
 * @returns {boolean} True when a notification should be shown.
 */
export const shouldNotifyUnread = ({previous, next, focused}) => (
    previous !== null && Number(next) > Number(previous) && !focused
);

export default {
    TRAY_REFRESH_MS,
    TIMER_SYNC_MS,
    formatTrayClock,
    buildTrayTitle,
    shouldNotifyUnread,
};
